import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Menu from '../menu/Menu';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import SelectPG from '../bedallocation/SelectPG';
import PaymentSuccessModal from './PaymentSuccessModal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaEdit, FaTimes } from 'react-icons/fa';
import { apiGet, apiPost } from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import '../common/rtCommon.css';
import './AddPayment.css';

const AddPayment = ({ onClose, preSelectedPG = null, preSelectedInvoiceId = null, singleInvoiceMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [selectedPG, setSelectedPG] = useState(preSelectedPG);
  const [showSelectPG, setShowSelectPG] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [loading, setLoading] = useState(true); // Start with loading true
  const [processing, setProcessing] = useState(false);
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [paymentSuccessData, setPaymentSuccessData] = useState(null);
  const [form, setForm] = useState({
    paymentMethodId: '',
    amount: '',
    referenceNumber: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const initializeComponent = async () => {
      await fetchPaymentMethods();
      
      // If we have pre-selected data, load it
      if (preSelectedPG && preSelectedInvoiceId) {
        await loadPreSelectedData();
      } else {
        // If no pre-selected data, set loading to false after fetching data
        setLoading(false);
      }
    };
    
    initializeComponent();
  }, []);

  useEffect(() => {
    if (selectedPG) {
      fetchUnpaidInvoices();
    }
  }, [selectedPG]);

  useEffect(() => {
    console.log('Payment methods state updated:', paymentMethods);
  }, [paymentMethods]);

  const fetchPaymentMethods = async () => {
    try {
      const response = await apiGet('/payments/methods');
      // Handle both cases: response.data or direct array response
      const paymentMethodsData = response?.data || response || [];
      
      // Filter only active payment methods
      const activeMethods = paymentMethodsData.filter(method => 
        method.status === 'ACTIVE' || method.status === 'active'
      );
      
      setPaymentMethods(activeMethods);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      toastError('Failed to load payment methods');
      // Set empty array on error
      setPaymentMethods([]);
    }
  };

  const fetchUnpaidInvoices = async () => {
    if (!selectedPG?.pg_id) return;

    try {
      setLoading(true);
      // Get unpaid invoices for the selected PG (sent, overdue, and partially_paid) in a single API call
      const response = await apiGet(`/invoicing/invoices?pg_id=${selectedPG.pg_id}&status=sent,overdue,partially_paid&limit=1000`);
      
      // Handle new response structure {invoices: [...], totalInvoicesAmount: number}
      const responseData = response?.data || [];
      const allInvoices = Array.isArray(responseData) ? responseData : (responseData.invoices || []);
      
      // Process invoices and filter only those with remaining amounts
      const invoicesWithRemaining = allInvoices.map(invoice => {
        // Use amount_remaining from backend if available, otherwise calculate
        const remainingAmount = invoice.amount_remaining !== undefined 
          ? invoice.amount_remaining 
          : (invoice.total_amount - (invoice.amount_paid || 0));
        
        return {
          ...invoice,
          remaining_amount: remainingAmount,
          is_selected: preSelectedInvoiceId && invoice.invoice_id === parseInt(preSelectedInvoiceId)
        };
      }).filter(invoice => invoice.remaining_amount > 0); // Only show invoices with remaining amounts
      
      // Sort invoices: selected invoice first, then others by date
      const sortedInvoices = invoicesWithRemaining.sort((a, b) => {
        if (preSelectedInvoiceId) {
          if (a.is_selected && !b.is_selected) return -1;
          if (!a.is_selected && b.is_selected) return 1;
        }
        return new Date(a.invoice_date) - new Date(b.invoice_date);
      });
      
      setUnpaidInvoices(sortedInvoices);
    } catch (error) {
      console.error('Error fetching unpaid invoices:', error);
      toastError('Failed to load unpaid invoices');
    } finally {
      setLoading(false);
    }
  };

  const loadPreSelectedData = async () => {
    if (!preSelectedPG || !preSelectedInvoiceId) return;

    try {
      setLoading(true);
      
      // Set the PG data first
      setSelectedPG(preSelectedPG);
      
      // The fetchUnpaidInvoices function will be called by the useEffect when selectedPG changes
      // It will handle fetching all invoices and marking the selected one
      
    } catch (error) {
      console.error('Error loading pre-selected data:', error);
      toastError('Failed to load invoice data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPG = (pg) => {
    setSelectedPG(pg);
    setShowSelectPG(false);
  };

  const handleEditPG = () => {
    setSelectedPG(null);
    setShowSelectPG(true);
  };

  const handleClearPG = () => {
    setSelectedPG(null);
    setUnpaidInvoices([]);
    setForm({
      paymentMethodId: '',
      amount: '',
      referenceNumber: '',
      notes: ''
    });
    setErrors({ ...errors, selectedPG: undefined });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const getTotalUnpaidAmount = () => {
    return unpaidInvoices.reduce((total, invoice) => total + (invoice.remaining_amount || 0), 0);
  };

  const getBalanceAfterPayment = () => {
    const totalUnpaid = getTotalUnpaidAmount();
    const paymentAmount = parseFloat(form.amount) || 0;
    return totalUnpaid - paymentAmount;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedPG) {
      newErrors.selectedPG = 'Paying Guest must be selected';
    }
    
    if (!form.paymentMethodId) {
      newErrors.paymentMethodId = 'Payment method is required';
    }
    
    if (!form.amount || parseFloat(form.amount) <= 0) {
      newErrors.amount = 'Valid payment amount is required';
    }
    
    const totalUnpaid = getTotalUnpaidAmount();
    const paymentAmount = parseFloat(form.amount) || 0;
    
    // Enhanced payment validation
    if (paymentAmount > totalUnpaid) {
      newErrors.amount = 'Excessive payment not allowed. Payment cannot exceed total unpaid amount.';
    }
    
    if (!paymentDate) {
      newErrors.paymentDate = 'Payment date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getPaymentWarnings = () => {
    const paymentAmount = parseFloat(form.amount) || 0;
    const totalUnpaid = getTotalUnpaidAmount();
    const warnings = [];

    // Determine which invoices will be paid with the current payment amount
    let invoicesToBePaid = [];
    let remainingPayment = paymentAmount;
    
    for (const invoice of unpaidInvoices) {
      if (remainingPayment <= 0) break;
      
      const invoiceRemaining = invoice.remaining_amount || 0;
      if (invoiceRemaining > 0) {
        const paymentForInvoice = Math.min(remainingPayment, invoiceRemaining);
        if (paymentForInvoice > 0) {
          invoicesToBePaid.push(invoice);
          remainingPayment -= paymentForInvoice;
        }
      }
    }
    
    // Check for active payment links in invoices that will be paid
    const activePaymentLinks = invoicesToBePaid.filter(invoice => {
      if (!invoice.payment_provider_link_status) return false;
      const status = invoice.payment_provider_link_status.toLowerCase();
      return ['generated', 'active', 'payment_initiated'].includes(status);
    });

    if (activePaymentLinks.length > 0) {
      let message;
      if (activePaymentLinks.length === 1 && invoicesToBePaid.length === 1) {
        // Only show invoice number if exactly one invoice will be paid
        message = `Active payment link will be cancelled for invoice: ${activePaymentLinks[0].invoice_number}`;
      } else {
        // Generic message for multiple invoices
        message = `Active payment links will be cancelled for invoices being paid`;
      }
      warnings.push({
        type: 'warning',
        message: message
      });
    }

    if (paymentAmount > 0 && paymentAmount < totalUnpaid) {
      if (singleInvoiceMode && unpaidInvoices.length === 1) {
        // Single invoice mode
        const invoice = unpaidInvoices[0];
        if (paymentAmount < invoice.remaining_amount) {
          warnings.push({
            type: 'warning',
            message: `This invoice will be partially paid. Remaining amount after payment: ₹${(invoice.remaining_amount - paymentAmount).toFixed(2)}`
          });
        }
      } else {
        // Multiple invoices mode
        warnings.push({
          type: 'info',
          message: 'Payment amount is less than total unpaid amount. One or more invoices may be partially paid.'
        });
      }
    }
    
    return warnings;
  };

  // Map payment type names to backend enum values
  // No longer need mapping since payment methods are stored in database
  // const mapPaymentMethod = (paymentTypeName) => {
  //   // Removed mapping function - now save payment method directly
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setProcessing(true);
      
      // Find the selected payment method
      const selectedPaymentMethod = paymentMethods.find(method => method.id === parseInt(form.paymentMethodId));
      
      // Prepare payment data with payment method and type strings
      const paymentData = {
        invoice_ids: unpaidInvoices.map(invoice => invoice.invoice_id), // Pass all unpaid invoice IDs
        pg_id: selectedPG.pg_id,
        amount: parseFloat(form.amount),
        payment_date: paymentDate.toISOString().split('T')[0],
        payment_method: selectedPaymentMethod?.name || 'Cash',
        payment_type: 'Payment', // Default payment type
        reference_number: form.referenceNumber || null,
        notes: form.notes || null,
        selected_invoice_id: preSelectedInvoiceId ? parseInt(preSelectedInvoiceId) : null
      };

      // Call the payment API
      const response = await apiPost('/payments', paymentData);
      const payment = response.data;
      
      if (payment && payment.payment_id) {
        // Payment was successful, prepare data for success modal
        // We need to fetch updated invoice details to show what was paid
        await fetchUnpaidInvoices(); // Refresh the invoice list
        
        const successData = {
          payment_created: payment,
          total_amount_processed: payment.amount,
          remaining_balance: getTotalUnpaidAmount(), // Get updated remaining balance
          pg_details: selectedPG,
          payment_date: paymentDate.toISOString().split('T')[0],
          payment_method: selectedPaymentMethod?.name || 'Cash',
          payment_type: 'Payment', // Default payment type name
          reference_number: form.referenceNumber,
          notes: form.notes,
          invoice_ids: JSON.parse(payment.invoice_ids || '[]') // Parse the invoice IDs from the payment
        };
        
        setPaymentSuccessData(successData);
        setShowSuccessModal(true);
        
      } else {
        toastError('Failed to process payment');
      }
      
    } catch (error) {
      console.error('Error processing payment:', error);
      
      let errorMessage = 'Failed to process payment';
      
      // Handle different types of error responses
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array of validation errors
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => {
            if (typeof err === 'object' && err.msg) {
              return err.msg;
            }
            return String(err);
          }).join('; ');
        }
        // If detail is a single validation error object
        else if (typeof detail === 'object' && detail.msg) {
          errorMessage = detail.msg;
        }
        // If detail is a string
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // If detail is something else, convert to string
        else {
          errorMessage = String(detail);
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toastError(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/payments');
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setPaymentSuccessData(null);
  };

  const handleNavigateToPayments = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/payments');
    }
  };

  // Show PG selector if showSelectPG is true
  if (showSelectPG) {
    return <SelectPG onSelect={handleSelectPG} allocated={true} />;
  }

  if (loading) {
    return (
      <div className="rt-layout">
        <Menu className="rt-menu" activePath={location.pathname} />
        <div className="rt-main-content">
          <RTSpinner message="Loading payment information..." fullScreen={true} />
        </div>
      </div>
    );
  }

  return (
    <div className="rt-layout">
      <Menu className="rt-menu" activePath={location.pathname} />
      <div className="rt-main-content">
        <div className="rt-title-row">
          <h2 className="rt-title">Add Payment</h2>
          <div className="rt-actions">
            <RTButton variant="secondary" onClick={handleCancel}>
              ← Back
            </RTButton>
          </div>
        </div>

        <div className="rt-main-section">
          <div className="add-payment-section">
            {/* Left Section - Payment Form */}
            <form onSubmit={handleSubmit} className="add-payment-form">
              <h2 className="rt-form-title">Payment Details</h2>
              
              {/* Paying Guest Selection */}
              <div className="rt-form-group">
                <label>
                  Paying Guest <span style={{ color: 'red' }}>*</span>
                  {preSelectedPG && <span style={{ fontSize: '12px', color: '#6c757d', marginLeft: '8px' }}>(Pre-selected)</span>}
                </label>
                <div className="pg-selection-container">
                  {!selectedPG ? (
                    <div 
                      className="pg-selection-empty clickable"
                      onClick={() => !preSelectedPG && setShowSelectPG(true)}
                      style={{ cursor: preSelectedPG ? 'not-allowed' : 'pointer', opacity: preSelectedPG ? 0.6 : 1 }}
                    >
                      <span style={{ fontWeight: 'bold'}}>Select Paying Guest</span>
                    </div>
                  ) : (
                    <div className="pg-selection-filled">
                      <span className="selected-pg-name">{selectedPG.name}</span>
                      {!preSelectedPG && (
                        <div className="pg-actions">
                          <button 
                            className="icon-button edit-button" 
                            onClick={handleEditPG}
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button 
                            className="icon-button clear-button" 
                            onClick={handleClearPG}
                            title="Clear"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {errors.selectedPG && (
                  <div className="rt-form-error">{errors.selectedPG}</div>
                )}
              </div>

              {/* Payment Date */}
              <div className="rt-form-group">
                <label>
                  Payment Date <span style={{ color: 'red' }}>*</span>
                </label>
                <DatePicker
                  selected={paymentDate}
                  onChange={(date) => setPaymentDate(date)}
                  dateFormat="dd/MM/yyyy"
                  className="custom-datepicker rt-input"
                  maxDate={new Date()} // Cannot be in the future
                  placeholderText="DD/MM/YYYY"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  autoComplete="off"
                  disabled={processing}
                />
                {errors.paymentDate && (
                  <div className="rt-form-error">{errors.paymentDate}</div>
                )}
              </div>

              {/* Payment Method */}
              <div className="rt-form-group">
                <label>
                  Payment Method <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  name="paymentMethodId"
                  value={form.paymentMethodId}
                  onChange={handleChange}
                  className="rt-input"
                  required
                  disabled={processing}
                >
                  <option value="">Select Payment Method</option>
                  {paymentMethods.map(method => (
                    <option key={method.id} value={method.id}>
                      {method.name}
                    </option>
                  ))}
                </select>
                {errors.paymentMethodId && (
                  <div className="rt-form-error">{errors.paymentMethodId}</div>
                )}
              </div>

              {/* Payment Amount */}
              <div className="rt-form-group">
                <label>
                  Payment Amount <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="number"
                  name="amount"
                  value={form.amount}
                  onChange={handleChange}
                  className="rt-input"
                  placeholder="Enter payment amount"
                  min="0"
                  step="0.01"
                  max={getTotalUnpaidAmount()}
                  required
                  disabled={processing}
                />
                {errors.amount && (
                  <div className="rt-form-error">{errors.amount}</div>
                )}
                {/* Payment Warnings */}
                {getPaymentWarnings().map((warning, index) => (
                  <div key={index} className={`add-payment-warning ${warning.type}`}>
                    <span className="warning-icon">
                      {warning.type === 'warning' ? '⚠️' : 'ℹ️'}
                    </span>
                    <span className="warning-text">{warning.message}</span>
                  </div>
                ))}
              </div>

              {/* Notes */}
              <div className="rt-form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  className="rt-input"
                  placeholder="Enter any additional notes (optional)"
                  rows="3"
                  disabled={processing}
                />
              </div>
            </form>

            {/* Right Section - Summary */}
            <div className="add-payment-summary">
              <h2 className="payment-summary-title">Payment Summary</h2>

              {/* Outstanding Invoices */}
              {loading ? (
                <div className="payment-summary-section">
                  <RTSpinner size="small" showMessage={true} message="Loading invoices..." />
                </div>
              ) : (
                <div className="add-payment-summary-section">
                  <h3 className="add-payment-summary-subtitle">Outstanding Invoices ({unpaidInvoices.length})</h3>
                  {unpaidInvoices.length > 0 ? (
                    <div>
                      {/* Invoice List */}
                      <div className="add-payment-invoice-list">
                        {unpaidInvoices.map((invoice, index) => {
                          const isSelected = invoice.is_selected;
                          const isGreyedOut = preSelectedInvoiceId && !isSelected;
                          
                          let cardClasses = "add-payment-invoice-card";
                          if (isSelected) cardClasses += " selected";
                          if (isGreyedOut) cardClasses += " greyed-out";
                          
                          return (
                            <div key={invoice.invoice_id} className={cardClasses}>
                              <div className="add-payment-invoice-row">
                                <div className="add-payment-invoice-left">
                                  <div className="add-payment-invoice-number">#{invoice.invoice_number}</div>
                                  <div className="add-payment-invoice-date">{new Date(invoice.invoice_date).toLocaleDateString()}</div>
                                </div>
                                <div className="add-payment-invoice-right">
                                  <div className="add-payment-invoice-amount">₹{(invoice.remaining_amount || invoice.total_amount).toFixed(2)}</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    selectedPG && (
                      <div className="add-payment-summary-item">
                        <span className="add-payment-summary-value" style={{ color: '#153C7F' }}>
                          No outstanding invoices
                        </span>
                      </div>
                    )
                  )}
                </div>
              )}

              {/* Amount Details */}
              <div className="add-payment-summary-section">
                <h3 className="add-payment-summary-subtitle">Amount Details</h3>
                <div className="add-payment-summary-item">
                  <span className="add-payment-summary-label">Total Amount Remaining:</span>
                  <span className="add-payment-summary-value">₹{getTotalUnpaidAmount().toFixed(2)}</span>
                </div>
                <div className="add-payment-summary-item">
                  <span className="add-payment-summary-label">Payment Amount:</span>
                  <span className="add-payment-summary-value">
                    ₹{form.amount ? parseFloat(form.amount).toFixed(2) : '0.00'}
                  </span>
                </div>
                {form.amount && (
                  <div className="add-payment-summary-item">
                    <span className="add-payment-summary-label">Balance After Payment:</span>
                    <span className="add-payment-summary-value" style={{ 
                      color: getBalanceAfterPayment() < 0 ? '#dc3545' : getBalanceAfterPayment() > 0 ? '#ff6b35' : '#153C7F',
                      fontWeight: 'bold'
                    }}>
                      ₹{getBalanceAfterPayment().toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="add-payment-summary-actions">
                <RTButton 
                  variant="secondary" 
                  onClick={handleCancel}
                  disabled={processing}
                  className="add-payment-action-button add-payment-cancel-button"
                >
                  Cancel
                </RTButton>
                <RTButton 
                  variant="primary" 
                  onClick={handleSubmit}
                  disabled={processing || !selectedPG || unpaidInvoices.length === 0 || !form.amount || getBalanceAfterPayment() < 0}
                  className="add-payment-action-button add-payment-submit-button"
                >
                  {processing ? 'Processing...' : 'Add Payment'}
                </RTButton>
              </div>
            </div>
          </div>

          {/* Processing Overlay */}
          {processing && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999
            }}>
              <RTSpinner size="medium" showMessage={true} message="Processing payment..." />
            </div>
          )}
        </div>
      </div>
      
      {/* Payment Success Modal */}
      <PaymentSuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        paymentData={paymentSuccessData}
        onNavigateToPayments={handleNavigateToPayments}
      />
    </div>
  );
};

export default AddPayment;
